import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackStylesHeight100, globalStackTokensGapSmall } from 'globalStyles';
import { KeyboardEvent, PointerEvent, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { getRandomId } from 'utils/string';

const keyboardStep: number = 24;
const hitAreaWidth: number = 8;

interface IResizableSplitProps {
  //the column that takes the remaining room
  main: ReactNode;
  //the resizable column at the right of main
  pane: ReactNode;
  //shown instead of the pane and its handle while the pane is closed, for example an expand button
  collapsedPane?: ReactNode;
  isPaneOpen: boolean;
  defaultWidth: number;
  minPaneWidth: number;
  //the main column always keeps this much room
  minMainWidth: number;
  //the most the pane takes of the width of the split, 0 to 1
  maxPaneShare: number;
  //where the width of the pane is stored; left out, the width is not kept
  storageKey?: LocalStorageKeys;
  //called once when a drag, a key press or a double-click ends
  onWidthChange?: (width: number) => void;
  //tooltip of the handle
  handleTitle?: string;
}

// A main column and a resizable pane at its right, with a drag handle between them. During a drag the
// width goes straight to the pane element: a state update on every pointer move would re-render both columns.
export const ResizableSplit = (props: IResizableSplitProps) => {
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const rootId = useRef(`isoplanner-split-${getRandomId()}`).current;
  const paneId = useRef(`isoplanner-split-pane-${getRandomId()}`).current;
  const [width, setWidth] = useState<number>(() => {
    const stored = props.storageKey
      ? Number.parseInt(getLocalStorageData(appContext, props.storageKey) ?? '', 10)
      : Number.NaN;

    return Number.isNaN(stored) ? props.defaultWidth : stored;
  });
  const [dragging, setDragging] = useState<boolean>(false);
  const dragWidth = useRef<number | undefined>(undefined);

  const restoreBody = () => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  //a drag that is still going when the split goes away must not leave the cursor behind
  useEffect(() => restoreBody, []);

  //closing the pane mid-drag removes the handle, so no pointer-up arrives to end the drag
  useEffect(() => {
    if (props.isPaneOpen) return;

    dragWidth.current = undefined;
    restoreBody();
    setDragging(false);
  }, [props.isPaneOpen]);

  const clampWidth = (newWidth: number, rowWidth: number): number => {
    const maxWidth = Math.max(
      props.minPaneWidth,
      Math.min(rowWidth * props.maxPaneShare, rowWidth - props.minMainWidth),
    );

    return Math.round(Math.max(props.minPaneWidth, Math.min(newWidth, maxWidth)));
  };

  const getRowWidth = (): number =>
    document.getElementById(rootId)?.getBoundingClientRect().width ?? Number.MAX_SAFE_INTEGER;

  const commitWidth = (newWidth: number) => {
    setWidth(newWidth);
    if (props.storageKey) setLocalStorageData(appContext, props.storageKey, newWidth.toString(), false);
    props.onWidthChange?.(newWidth);
  };

  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    const pane = document.getElementById(paneId);
    if (e.button !== 0 || !pane) return;

    e.currentTarget.setPointerCapture(e.pointerId);
    dragWidth.current = pane.getBoundingClientRect().width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    setDragging(true);
  };

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    const pane = document.getElementById(paneId);
    if (dragWidth.current === undefined || !pane) return;

    dragWidth.current = clampWidth(pane.getBoundingClientRect().right - e.clientX, getRowWidth());
    pane.style.width = `${dragWidth.current}px`;
  };

  const onPointerEnd = (e: PointerEvent<HTMLElement>) => {
    if (dragWidth.current === undefined) return;

    const newWidth = dragWidth.current;
    dragWidth.current = undefined;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    restoreBody();
    setDragging(false);
    commitWidth(newWidth);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const pane = document.getElementById(paneId);
    if (!pane || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;

    e.preventDefault();
    const delta = e.key === 'ArrowLeft' ? keyboardStep : -keyboardStep;
    commitWidth(clampWidth(pane.getBoundingClientRect().width + delta, getRowWidth()));
  };

  const lineColor = theme.palette.themePrimary;
  //the handle and the two gaps around it also take room from the row
  const handleRoom = hitAreaWidth + 2 * Number(globalStackTokensGapSmall.childrenGap);

  return (
    <IPStack horizontal id={rootId} styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
      <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
        {props.main}
      </IPStack.Item>
      {props.isPaneOpen && (
        <>
          <IPStack.Item
            tabIndex={0}
            title={props.handleTitle}
            styles={{
              root: {
                width: hitAreaWidth,
                flexShrink: 0,
                position: 'relative',
                cursor: 'col-resize',
                outline: 'none',
                touchAction: 'none',
                selectors: {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '50%',
                    width: 1,
                    background: dragging ? lineColor : theme.palette.neutralLight,
                  },
                  '&:hover::after, &:focus::after': { background: lineColor },
                },
              },
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
            onLostPointerCapture={onPointerEnd}
            onKeyDown={onKeyDown}
            onDoubleClick={() => commitWidth(props.defaultWidth)}
          >
            {/* Stack.Item renders nothing without children, so the handle carries an empty stack */}
            <IPStack styles={globalStackStylesHeight100} />
          </IPStack.Item>
          <IPStack.Item
            id={paneId}
            styles={{
              root: {
                width,
                flexShrink: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                //a stored width never squeezes the main column below its own minimum or passes the share,
                //also when the row gets narrower after the drag
                maxWidth: `min(${props.maxPaneShare * 100}%, calc(100% - ${props.minMainWidth + handleRoom}px))`,
              },
            }}
          >
            {props.pane}
          </IPStack.Item>
        </>
      )}
      {!props.isPaneOpen && props.collapsedPane}
    </IPStack>
  );
};
